import React, { useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { gql, useMutation, useQuery } from "urql";
import { Formik, Form } from "formik";

import { WireOpPaymentInput, WireTransaction } from "../../../../../graphql/src/wire.types";
import { Alert, AlertVariant } from "../../../../../components/src/Alert";
import { DateHelper } from "../../../../../common/src";
import { OpPaymentStatusEnum, TransactionItemType, TransactionStatus, TransactionType } from "../../../types.generated";
import { numberToDecimalString } from "../../../common/sie/sieFunctions";
import { OP_PAYMENTS_QUERY } from "../../../../../graphql/src/oppayment/queries";
import { useAlertTimeOut } from "../../../../../components/src/common/Utils";
import { DateField } from "../../../../../components/src/form";

const FEE_TRANSACTIONS = gql`
    query transactions($filter: TransactionFilterInput) {
        transactions(filter: $filter) {
            _id
            clientId
            client {
                fundInfo {
                    fundCompany {
                        name
                        accounts {
                            defaultAccount
                            defaultCashInstruments {
                                currency
                                partyInstrumentId
                                partyInstrument {
                                    _id
                                    currency
                                    bic
                                    iban
                                }
                            }
                        }
                    }
                }
            }
            items {
                amount
                currency
                type
                instrument {
                    _id
                    bic
                    iban
                    name
                }
            }
        }
    }
`;

export function CreateOpPaymentsButton(): React.ReactElement {
    const today = DateHelper.dateToString(DateHelper.today()).substring(0, 10);
    const minDate = DateHelper.dateToString(DateHelper.addDays(DateHelper.today(), -7)).substring(0, 10);
    const [selectedDate, setSelectedDate] = useState<string>(today);

    const [spinner, setSpinner] = useState(false);
    const creatingRef = useRef(false);
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    const [{ data: dataPayments }, refetchPayments] = useQuery({
        query: OP_PAYMENTS_QUERY,
        variables: {
            filter: { startDate: selectedDate }
        }
    });

    const CREATE_OP_PAYMENTS = gql`
        mutation opCreatePayments($payments: [OPPaymentInput!]!) {
            opCreatePayments(payments: $payments) {
                _id
            }
        }
    `;
    const [____, opCreatePayments] = useMutation(CREATE_OP_PAYMENTS);

    const [{ data: dataFeeTransactions }] = useQuery({
        query: FEE_TRANSACTIONS,
        variables: {
            filter: {
                typesIn: [TransactionType.Fee],
                statusNotIn: [TransactionStatus.Deleted],
                valueDateStart: selectedDate,
                valueDateEnd: selectedDate
            }
        }
    });

    // Management fee payments Grouped by date and reference
    // Management cost payments Grouped by date and clientId
    const paymentsByDateByReferenceOrClientId: {
        managementFees: Record<string, Record<string, WireOpPaymentInput[]>>;
        managementCosts: Record<string, Record<string, WireOpPaymentInput[]>>;
    } = useMemo(() => {
        const paymentManagementFeesByDateByReference: Record<string, Record<string, WireOpPaymentInput[]>> = {};
        const paymentManagementCostsByDateByClientId: Record<string, Record<string, WireOpPaymentInput[]>> = {};
        if (dataPayments && dataPayments.opPayments) {
            for (const payment of dataPayments.opPayments) {
                if (payment.transactionStatus !== OpPaymentStatusEnum.CANC && payment.transactionStatus !== OpPaymentStatusEnum.RJCT) {
                    if (
                        payment.requestedExecutionDate &&
                        payment.remittanceInformationUnstructured &&
                        payment.remittanceInformationUnstructured.includes("Arvode")
                    ) {
                        if (paymentManagementFeesByDateByReference[payment.requestedExecutionDate]) {
                            if (
                                paymentManagementFeesByDateByReference[payment.requestedExecutionDate][
                                    payment.remittanceInformationUnstructured
                                ]
                            ) {
                                paymentManagementFeesByDateByReference[payment.requestedExecutionDate][
                                    payment.remittanceInformationUnstructured
                                ].push(payment);
                            } else {
                                paymentManagementFeesByDateByReference[payment.requestedExecutionDate][
                                    payment.remittanceInformationUnstructured
                                ] = [payment];
                            }
                        } else {
                            paymentManagementFeesByDateByReference[payment.requestedExecutionDate] = {
                                [payment.remittanceInformationUnstructured]: [payment]
                            };
                        }
                    }
                    if (
                        payment.requestedExecutionDate &&
                        payment.clientId &&
                        payment.remittanceInformationUnstructured &&
                        payment.remittanceInformationUnstructured.includes("Konto 1689")
                    ) {
                        if (paymentManagementCostsByDateByClientId[payment.requestedExecutionDate]) {
                            if (paymentManagementCostsByDateByClientId[payment.requestedExecutionDate][payment.clientId]) {
                                paymentManagementCostsByDateByClientId[payment.requestedExecutionDate][payment.clientId].push(payment);
                            } else {
                                paymentManagementCostsByDateByClientId[payment.requestedExecutionDate][payment.clientId] = [payment];
                            }
                        } else {
                            paymentManagementCostsByDateByClientId[payment.requestedExecutionDate] = {
                                [payment.clientId]: [payment]
                            };
                        }
                    }
                }
            }
        }
        return { managementFees: paymentManagementFeesByDateByReference, managementCosts: paymentManagementCostsByDateByClientId };
    }, [dataPayments]);

    const managementFeesAndManagementCosts: { managementFees: WireOpPaymentInput[]; managementCosts: WireOpPaymentInput[] } | undefined =
        useMemo(() => {
            const managementFees: WireOpPaymentInput[] = [];
            const managementCosts: WireOpPaymentInput[] = [];
            if (dataFeeTransactions && dataFeeTransactions.transactions) {
                for (const transactionData of dataFeeTransactions.transactions) {
                    const transaction: WireTransaction = transactionData;
                    // Find management fee, management cost and settlement amount items
                    const managementFeeItem = transaction.items.find((item) => item.type === TransactionItemType.ManagementFee);
                    const managementCostItem = transaction.items.find((item) => item.type === TransactionItemType.ManagementCost);
                    const settlementAmountItem = transaction.items.find((item) => item.type === TransactionItemType.SettlementAmount);

                    if (managementFeeItem && settlementAmountItem) {
                        if (transaction.client?.fundInfo?.fundCompany) {
                            const fundCompany = transaction.client?.fundInfo?.fundCompany;
                            const defaultAccount = fundCompany.accounts?.find((account) => account.defaultAccount);
                            const defaultCashInstrument = defaultAccount?.defaultCashInstruments?.find(
                                (instrument) => instrument.currency === settlementAmountItem.currency
                            );

                            const reference = `Arvode ${managementFeeItem.instrument?.name}`;

                            const existingPayment =
                                paymentsByDateByReferenceOrClientId?.managementFees[selectedDate]?.[reference] &&
                                paymentsByDateByReferenceOrClientId?.managementFees[selectedDate]?.[reference].length > 0;

                            if (defaultCashInstrument?.partyInstrument) {
                                if (!existingPayment) {
                                    managementFees.push({
                                        instructedAmount: {
                                            amount: numberToDecimalString(Math.abs(settlementAmountItem.amount), 2),
                                            currency: settlementAmountItem.currency
                                        },
                                        remittanceInformationUnstructured: reference,
                                        debtorAccount: { iban: settlementAmountItem.instrument?.iban },
                                        creditorAccount: { iban: defaultCashInstrument.partyInstrument.iban },
                                        creditorAgent: defaultCashInstrument.partyInstrument.bic,
                                        creditorName: fundCompany.name?.substring(0, 35), //Maximum 35 characters allowed
                                        debtorAccountMessage: `Management fee`,
                                        endToEndIdentification: `Management fee`
                                    });
                                }
                            } else {
                                console.error(
                                    `No default cash instrument found for transaction ${transaction._id}, clientId: ${transaction.clientId}`
                                );
                            }
                        } else {
                            console.error(
                                `client.fundInfo.fundCompany not found for transaction ${transaction._id}, clientId: ${transaction.clientId}`
                            );
                        }
                    }

                    if (managementCostItem && settlementAmountItem) {
                        if (transaction.client?.fundInfo?.fundCompany) {
                            const fundCompany = transaction.client?.fundInfo?.fundCompany;
                            const defaultAccount = fundCompany.accounts?.find((account) => account.defaultAccount);
                            const defaultCashInstrument = defaultAccount?.defaultCashInstruments?.find(
                                (instrument) => instrument.currency === settlementAmountItem.currency
                            );

                            const reference = `Konto 1689`;

                            // Mgmt costs are not booked on class instrument but still by class.
                            // Below is best effort to check for existing cost payments by client instead of class.
                            const existingPayment =
                                paymentsByDateByReferenceOrClientId?.managementCosts[selectedDate]?.[transaction.clientId] &&
                                paymentsByDateByReferenceOrClientId?.managementCosts[selectedDate][transaction.clientId].length > 0;

                            if (defaultCashInstrument?.partyInstrument) {
                                if (!existingPayment) {
                                    managementCosts.push({
                                        instructedAmount: {
                                            amount: numberToDecimalString(Math.abs(settlementAmountItem.amount), 2),
                                            currency: settlementAmountItem.currency
                                        },
                                        remittanceInformationUnstructured: reference,
                                        debtorAccount: { iban: settlementAmountItem.instrument?.iban },
                                        creditorAccount: { iban: defaultCashInstrument.partyInstrument.iban },
                                        creditorAgent: defaultCashInstrument.partyInstrument.bic,
                                        creditorName: fundCompany.name?.substring(0, 35), //Maximum 35 characters allowed
                                        debtorAccountMessage: `Management cost`,
                                        endToEndIdentification: `Management cost`
                                    });
                                }
                            } else {
                                console.error(
                                    `No default cash instrument found for transaction ${transaction._id}, clientId: ${transaction.clientId}`
                                );
                            }
                        } else {
                            console.error(
                                `client.fundInfo.fundCompany not found for transaction ${transaction._id}, clientId: ${transaction.clientId}`
                            );
                        }
                    }
                }
            }
            return { managementFees: managementFees, managementCosts: managementCosts };
        }, [dataFeeTransactions, paymentsByDateByReferenceOrClientId, selectedDate]);

    return (
        <>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-6/12 px-2 mb-4 flex mt-3">
                    <p>
                        Management fees/costs can be created as long as at least one management fee/cost for the selected date has not been
                        created yet. (Only remaining ones will be created.)
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-4/12 px-2 mb-4">
                    <Formik
                        enableReinitialize={true}
                        initialValues={{ selectedDate: selectedDate }}
                        onSubmit={() => {
                            // Empty function since we don't need form submission
                        }}
                    >
                        {({ values }) => {
                            if (values.selectedDate !== selectedDate) {
                                setSelectedDate(values.selectedDate);
                            }
                            return (
                                <Form>
                                    <DateField
                                        name="selectedDate"
                                        label="Payment Date"
                                        className=""
                                        disabled={false}
                                        minDate={minDate}
                                        maxDate={today}
                                    />
                                </Form>
                            );
                        }}
                    </Formik>
                </div>
            </div>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    {alert.visible ? (
                        <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                            {alert.message}
                        </Alert>
                    ) : null}
                </div>
            </div>

            {spinner ? (
                <div className="flex items-center gap-2 my-3">
                    <Loader2 size={20} className="animate-spin" />
                    <span>Updating...</span>
                </div>
            ) : null}

            <button
                type="button"
                className="btn btn-secondary mt-2"
                onClick={async () => {
                    // setState disables the button on the next render. A second click before that still runs.
                    if (creatingRef.current) {
                        return;
                    }
                    creatingRef.current = true;
                    setSpinner(true);
                    let allPayments: WireOpPaymentInput[] = [];
                    if (managementFeesAndManagementCosts) {
                        if (managementFeesAndManagementCosts.managementFees && managementFeesAndManagementCosts.managementFees.length > 0) {
                            allPayments = allPayments.concat(managementFeesAndManagementCosts.managementFees);
                        }
                        if (
                            managementFeesAndManagementCosts.managementCosts &&
                            managementFeesAndManagementCosts.managementCosts.length > 0
                        ) {
                            allPayments = allPayments.concat(managementFeesAndManagementCosts.managementCosts);
                        }
                    }
                    await opCreatePayments({ payments: allPayments })
                        .then((result) => {
                            if (result.error) {
                                setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                return;
                            }

                            setAlert({
                                color: "success",
                                visible: true,
                                message: `Management fees/costs were created successfully!`
                            });
                            refetchPayments({ requestPolicy: "network-only" });
                        })
                        .catch((error) => {
                            setAlert({ color: "danger", visible: true, message: error.toString() });
                        })
                        .finally(() => {
                            creatingRef.current = false;
                            setSpinner(false);
                        });
                }}
                disabled={
                    spinner ||
                    (managementFeesAndManagementCosts?.managementFees?.length === 0 &&
                        managementFeesAndManagementCosts?.managementCosts?.length === 0) ||
                    !managementFeesAndManagementCosts
                }
            >
                Create management fee/cost payments for {selectedDate}
            </button>
        </>
    );
}
